import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { SiteThemeConfig } from "@site-factory/config";
import { resolveTheme, type ThemeSelection } from "./types";

interface ThemeProviderProps {
  /** Theme selection - can be theme name, theme with overrides, or inline config */
  theme: ThemeSelection;
  /** Child components */
  children: ReactNode;
  /** Optional controlled mode */
  mode?: "light" | "dark";
  /** Notify when mode changes (for controlled usage) */
  onModeChange?: (mode: "light" | "dark") => void;
  /** Persist mode to localStorage (default: true) */
  persist?: boolean;
}

/**
 * Applies CSS variables from the site theme configuration.
 * Wrap your app with this component to enable theming.
 */
function applyTheme(theme: SiteThemeConfig) {
  const root = document.documentElement;

  // Core colors
  root.style.setProperty("--color-primary", theme.primary);
  root.style.setProperty("--color-secondary", theme.secondary);
  root.style.setProperty("--color-accent", theme.accent);
  root.style.setProperty("--color-background", theme.background);
  root.style.setProperty("--color-text", theme.text);

  // Extended colors (with fallbacks)
  root.style.setProperty("--color-muted", theme.muted ?? "#64748b");
  root.style.setProperty("--color-border", theme.border ?? "#e2e8f0");
  root.style.setProperty("--color-primary-foreground", theme.primaryForeground ?? "#ffffff");
  root.style.setProperty("--color-secondary-foreground", theme.secondaryForeground ?? "#ffffff");
  root.style.setProperty("--color-accent-foreground", theme.accentForeground ?? theme.text);
  root.style.setProperty("--color-danger", theme.danger ?? "#ef4444");
  root.style.setProperty("--color-danger-foreground", theme.dangerForeground ?? "#ffffff");
  root.style.setProperty("--color-ring", theme.ring ?? theme.primary);

  // Surfaces
  root.style.setProperty("--color-surface", theme.surface ?? theme.background);
  root.style.setProperty("--color-surface-alt", theme.surfaceAlt ?? "#f8fafc");
  root.style.setProperty("--color-surface-elevated", theme.surfaceElevated ?? "#f1f5f9");
  root.style.setProperty("--color-surface-2", theme.surface2 ?? theme.surfaceAlt ?? "#f1f5f9");

  // Radius scale
  root.style.setProperty("--radius-sm", theme.radiusSm ?? "0.375rem");
  root.style.setProperty("--radius-md", theme.radiusMd ?? "0.5rem");
  root.style.setProperty("--radius-lg", theme.radiusLg ?? "0.75rem");
  root.style.setProperty("--radius-card", theme.cardRadius);
  root.style.setProperty("--radius-button", theme.buttonRadius);

  // Shadow scale
  root.style.setProperty("--shadow-sm", theme.shadowSm ?? "0 1px 2px 0 rgb(0 0 0 / 0.05)");
  root.style.setProperty("--shadow-md", theme.shadowMd ?? "0 4px 6px -1px rgb(0 0 0 / 0.1)");
  root.style.setProperty("--shadow-lg", theme.shadowLg ?? "0 10px 15px -3px rgb(0 0 0 / 0.1)");
  root.style.setProperty("--shadow-card", theme.cardShadow);

  // Gradients
  const gradientStrong =
    theme.gradientHeroStrong ??
    `radial-gradient(circle at 18% 18%, ${theme.accent ?? theme.primary}33 0%, ${theme.primary}66 28%, ${theme.secondary}99 60%, ${theme.primary} 100%)`;
  const gradientSoft =
    theme.gradientHeroSoft ??
    `radial-gradient(circle at 24% 24%, ${theme.primary}26 0%, ${theme.accent ?? theme.primary}26 32%, ${theme.secondary}33 65%, ${theme.background} 100%)`;
  const surfaceBand =
    theme.gradientSurfaceBand ??
    `radial-gradient(circle at 15% 20%, ${theme.accent}0d, transparent 40%), radial-gradient(circle at 80% 10%, ${theme.secondary}0d, transparent 45%), ${theme.background}`;
  root.style.setProperty("--gradient-hero-strong", gradientStrong);
  root.style.setProperty("--gradient-hero-soft", gradientSoft);
  root.style.setProperty("--gradient-surface-band", surfaceBand);

  document.body.style.backgroundColor = theme.background;
  document.body.style.color = theme.text;
}

export function ThemeProvider({ theme: themeSelection, children, mode: controlledMode, onModeChange, persist = true }: ThemeProviderProps) {
  // Resolve theme selection to SiteThemeConfig
  const theme = useMemo(() => resolveTheme(themeSelection), [themeSelection]);

  const [internalMode, setInternalMode] = useState<"light" | "dark">(() => {
    if (controlledMode) return controlledMode;
    if (typeof window !== "undefined" && persist) {
      const stored = localStorage.getItem("sf-theme-mode");
      if (stored === "light" || stored === "dark") return stored;
    }
    return "light";
  });

  const mode = controlledMode ?? internalMode;
  const activeTheme = useMemo(() => {
    if (mode === "dark" && theme.dark) {
      return { ...theme, ...theme.dark };
    }
    return theme;
  }, [mode, theme]);

  useEffect(() => {
    applyTheme(activeTheme);
    if (!controlledMode && persist && typeof window !== "undefined") {
      localStorage.setItem("sf-theme-mode", mode);
    }
  }, [activeTheme, mode, controlledMode, persist]);

  useEffect(() => {
    return () => {
      const root = document.documentElement;
      // Core colors
      root.style.removeProperty("--color-primary");
      root.style.removeProperty("--color-secondary");
      root.style.removeProperty("--color-accent");
      root.style.removeProperty("--color-background");
      root.style.removeProperty("--color-text");
      // Extended colors
      root.style.removeProperty("--color-muted");
      root.style.removeProperty("--color-border");
      root.style.removeProperty("--color-primary-foreground");
      root.style.removeProperty("--color-secondary-foreground");
      root.style.removeProperty("--color-accent-foreground");
      root.style.removeProperty("--color-danger");
      root.style.removeProperty("--color-danger-foreground");
      root.style.removeProperty("--color-ring");
      // Surfaces
      root.style.removeProperty("--color-surface");
      root.style.removeProperty("--color-surface-alt");
      root.style.removeProperty("--color-surface-elevated");
      root.style.removeProperty("--color-surface-2");
      // Gradients
      root.style.removeProperty("--gradient-hero-strong");
      root.style.removeProperty("--gradient-hero-soft");
      root.style.removeProperty("--gradient-surface-band");
      // Radius scale
      root.style.removeProperty("--radius-sm");
      root.style.removeProperty("--radius-md");
      root.style.removeProperty("--radius-lg");
      root.style.removeProperty("--radius-card");
      root.style.removeProperty("--radius-button");
      // Shadow scale
      root.style.removeProperty("--shadow-sm");
      root.style.removeProperty("--shadow-md");
      root.style.removeProperty("--shadow-lg");
      root.style.removeProperty("--shadow-card");
    };
  }, []);

  const handleToggle = (next: "light" | "dark") => {
    if (controlledMode) {
      onModeChange?.(next);
    } else {
      setInternalMode(next);
    }
  };

  return (
    <>
      {children}
      <button
        className="sr-only"
        aria-hidden
        onClick={() => handleToggle(mode === "light" ? "dark" : "light")}
        tabIndex={-1}
      />
    </>
  );
}

/**
 * Generate inline style object from theme config.
 * Useful for SSR or when you can't use ThemeProvider.
 */
export function getThemeStyles(theme: SiteThemeConfig): Record<string, string> {
  return {
    "--color-primary": theme.primary,
    "--color-secondary": theme.secondary,
    "--color-accent": theme.accent,
    "--color-background": theme.background,
    "--color-text": theme.text,
    "--color-surface": (theme as any).surface ?? theme.background,
    "--color-surface-alt": (theme as any).surfaceAlt ?? "#f8fafc",
    "--color-surface-elevated": (theme as any).surfaceElevated ?? "#f1f5f9",
    "--radius-card": theme.cardRadius,
    "--radius-button": theme.buttonRadius,
    "--shadow-card": theme.cardShadow,
  };
}

/**
 * Generate a CSS class string with theme variables.
 * Useful for applying theme at a specific scope.
 */
export function getThemeClassName(): string {
  return "site-theme";
}
